import { StyleUtil } from 'utils/StyleUtil'
import closeSvg from '../images/close2.svg'
import Spinner from './Spinner'
import { useCallback, useEffect, useRef, useState } from 'react'
import DropDownMenu, { DropDownItem, DropDownMenuRefType } from './DropDownMenu'
import sortSvg from 'images/sort.svg'
import sort2Svg from 'images/sort2.svg'
import { Tooltip } from 'react-tooltip'
import { ToastUtil } from 'utils/ToastUtil'
import { GeneratedQuestion, QuizQuestion } from 'apis/entities/quiz.entity'
import { useCompositeAuth } from 'providers/CompositeAuthProvider'
import Path from 'routes/Path'
import { useNavigate } from 'react-router-dom'
import { getDateText, getTimeText } from 'utils/StringUtil'
import LargeSpinner from './LargeSpinner'
import Emitter, { Events } from 'core/emitter'
import {
  ArchClient,
  ArchCourse,
  ArchIntake,
  ArchModule,
  ArchProgram,
  ArchTopic,
} from 'apis/entities/architecture.entity'
import { sortIntakeOptionsByCreatedAt } from 'utils/ArchUtil'
import { useArchitectureApi } from 'providers/ArchitectureApiProvider'
import { Direction, DirectionAsc, DirectionDesc } from 'utils/TableUtils'
import ButtonSpinner from './ButtonSpinner'
import { useQuizApi } from 'providers/QuizApiProvider'

const ClientOptionLabel = {
  id: '0',
  name: 'Clients',
  value: '0',
  isLabel: true,
}

const ProgramOptionLabel = {
  id: '0',
  name: 'Programs',
  value: '0',
  isLabel: true,
}

const IntakeOptionLabel = {
  id: '0',
  name: 'Cohorts',
  value: '0',
  isLabel: true,
}

interface Props {
  multiple: boolean
  onGenerated: () => void
  onClose: () => void
  // for changing the topic of the question
  quizId?: string
  quizQuestion?: QuizQuestion
  onSaved?: () => void
  // quiz is mapped to a course
  courseId?: string
  'data-testid'?: string
}

export default function WidgetQuestionGenerator({
  multiple,
  onGenerated,
  onClose,
  quizId,
  quizQuestion,
  onSaved,
  courseId,
  'data-testid': dataTestId,
}: Props): JSX.Element {
  const navigate = useNavigate()
  const { user } = useCompositeAuth()
  const [isFetched, setIsFetched] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const { generateMultipleQuestionsV2, createQuiz, updateQuizQuestion } =
    useQuizApi()
  const { getArchClients, getArchClient, getArchCourse } = useArchitectureApi()
  const [orderByTopic, setOrderByTopic] = useState<Direction>('')
  const [orderByPublishedBy, setOrderByPublishedBy] = useState<Direction>('')
  const [orderByDateTime, setOrderByDateTime] = useState<Direction>('')
  const refNumberOfQuestions = useRef<number>(30)
  const [checkedIds, setCheckedIds] = useState<string[]>([])
  const [selectedTopicId, setSelectedTopicId] = useState<string>('')

  const [isFetchingClients, setIsFetchingClients] = useState(false)
  // const [clients, setClients] = useState<ArchClient[]>([])

  const [clientOptions, setClientOptions] = useState<any>([ClientOptionLabel])
  const [programOptions, setProgramOptions] = useState<any>([
    ProgramOptionLabel,
  ])
  const [intakeOptions, setIntakeOptions] = useState<any>([IntakeOptionLabel])

  const refDropDownMenuClients = useRef<DropDownMenuRefType>(null)
  const refDropDownMenuPrograms = useRef<DropDownMenuRefType>(null)
  const refDropDownMenuIntakes = useRef<DropDownMenuRefType>(null)

  const [selectedClient, setSelectedClient] = useState<ArchClient | null>(null)
  const [selectedProgram, setSelectedProgram] = useState<ArchProgram | null>(
    null,
  )
  const [selectedIntake, setSelectedIntake] = useState<ArchIntake | null>(null)
  const [selectedCourse, setSelectedCourse] = useState<ArchCourse | null>(null)
  const [selectedModules, setSelectedModules] = useState<ArchModule[]>([])

  // for direct access without state
  const refSelectedClient = useRef<ArchClient | null>(null)
  const refSelectedProgram = useRef<ArchProgram | null>(null)
  const refSelectedIntake = useRef<ArchIntake | null>(null)
  const refSelectedCourse = useRef<ArchCourse | null>(null)
  const refSelectedModules = useRef<ArchModule[]>([])

  const [courses, setCourses] = useState<ArchCourse[]>([])
  const [modules, setModules] = useState<ArchModule[]>([])
  const [topics, setTopics] = useState<ArchTopic[]>([])

  const [isSaving, setIsSaving] = useState(false)
  const isChangingTopic = !!quizQuestion

  const tooltipStyle = {
    backgroundColor: '#fff',
    color: '#000',
    borderRadius: '6px',
  }

  const tooltipStyleReverse = {
    backgroundColor: '#6b69c1',
    color: '#fff',
    borderRadius: '6px',
  }

  enum SortBy {
    Topic = 'topic',
    PublishedBy = 'publishedUserName',
    DateTime = 'updatedAt',
  }

  const [sortConfig, setSortConfig] = useState<{
    key: SortBy
    direction: Direction
  } | null>(null)

  const getTooptipStyle = (key: SortBy): any => {
    switch (key) {
      case SortBy.Topic:
        return orderByTopic === DirectionAsc
          ? tooltipStyleReverse
          : tooltipStyle
      case SortBy.PublishedBy:
        return orderByPublishedBy === DirectionAsc
          ? tooltipStyleReverse
          : tooltipStyle
      case SortBy.DateTime:
        return orderByDateTime === DirectionAsc
          ? tooltipStyleReverse
          : tooltipStyle
      default:
        return tooltipStyle
    }
  }

  const getSortingText = (key: SortBy): string => {
    switch (key) {
      case SortBy.Topic:
        if (orderByTopic === DirectionAsc) return 'Sort Z to A'
        return 'Sort A to Z'
      case SortBy.PublishedBy:
        if (orderByPublishedBy === DirectionAsc) return 'Sort Z to A'
        return 'Sort A to Z'
      case SortBy.DateTime:
        if (orderByDateTime === DirectionAsc) return 'Sort new to old'
        return 'Sort old to new'
      default:
        return ''
    }
  }

  const createQuizByGenerator = useCallback(
    async (questions: GeneratedQuestion[]) => {
      try {
        const auth0Id = user?.auth0Id || user?.id
        const username = user?.username
        const firstName = user?.firstName
        const email = user?.email

        const quizQuestions = questions.map((q, i) => {
          return {
            text: q.questionText,
            learningOutcome: q.learningOutcome,
            answers: [q.correctAnswer, q.incorrectAnswer1, q.incorrectAnswer2],
            archTopicId: q.archTopicId,
            position: i + 1,
          } as QuizQuestion
        })

        const quiz = await createQuiz(
          ' ', // space for no title
          '',
          auth0Id,
          username,
          firstName,
          email,
          quizQuestions,
        )
        if (quiz) {
          onGenerated()
          navigate(`${Path.quizzes.path}/${quiz.id}`)
        }
      } catch (error) {
        console.error('Failed to create quiz by generator', error)
      } finally {
        setIsGenerating(false)
      }
    },
    [user, createQuiz, onGenerated, navigate],
  )

  const onClickSort = (key: SortBy) => {
    let ascending = true
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === DirectionAsc
    ) {
      ascending = false
    }

    switch (key) {
      case SortBy.Topic:
        setOrderByTopic(ascending ? DirectionAsc : DirectionDesc)
        break
      case SortBy.PublishedBy:
        setOrderByPublishedBy(ascending ? DirectionAsc : DirectionDesc)
        break
      case SortBy.DateTime:
        setOrderByDateTime(ascending ? DirectionAsc : DirectionDesc)
        break
      default:
        break
    }

    setSortConfig({ key, direction: ascending ? DirectionAsc : DirectionDesc })
  }

  const onSelectNumberOfQuestions = (item: DropDownItem) => {
    const value = item.value as number
    refNumberOfQuestions.current = value
  }

  const onClickGenerate = async () => {
    if (multiple) {
      generateMultipleQuestions()
    } else {
      generateNewQuestion()
    }
  }

  const canGenerate = (): boolean => {
    if (multiple) {
      return selectedModules.length > 0 && checkedIds.length > 0
    } else {
      return !!selectedTopicId
    }
  }

  const generateNewQuestion = async () => {
    console.log('selectedTopicId', selectedTopicId)
    if (!selectedTopicId) {
      ToastUtil.warning('Please select a topic')
      return
    }
    Emitter.emit(Events.GenerateNewQuestion, {
      topicId: selectedTopicId,
    })
    onClose()
  }

  const generateMultipleQuestions = async () => {
    const topicIds = checkedIds
    // console.log('topicIds', topicIds)
    const numberOfQuestions = refNumberOfQuestions.current
    console.log('numberOfQuestions', numberOfQuestions)
    if (topicIds.length === 0) {
      ToastUtil.warning('Please select a module which has topics')
      return
    }
    if (!numberOfQuestions) {
      ToastUtil.warning('Please select number of questions')
      return
    }

    try {
      setIsGenerating(true)
      const res = await generateMultipleQuestionsV2(topicIds, numberOfQuestions)
      console.log('res', res)
      const questions = res.questions
      console.log('questions', questions)
      await createQuizByGenerator(questions as GeneratedQuestion[])
    } catch (error) {
      console.error('Failed to generate multiple questions', error)
      ToastUtil.error('Failed to generate questions')
    } finally {
      setIsGenerating(false)
    }
  }

  const updateProgramOptions = () => {
    // set all program options by distinct program names
    const tmpProgramOptions: any[] = []

    const programs = refSelectedClient.current?.programs || []
    programs.forEach((program) => {
      if (!tmpProgramOptions.find((p) => p.name === program.name)) {
        tmpProgramOptions.push({
          id: program.id,
          name: program.name,
          value: program.id,
        })
      }
    })
    // console.log('tmpProgramOptions', tmpProgramOptions)

    // sort program options by alphabet ascending
    tmpProgramOptions.sort((a, b) => {
      if (a.name < b.name) {
        return -1
      }
      if (a.name > b.name) {
        return 1
      }
      return 0
    })

    setProgramOptions([ProgramOptionLabel, ...tmpProgramOptions])
  }

  const updateIntakeOptions = () => {
    // set all intake options by distinct intake names
    const tmpIntakeOptions: any[] = []

    // show selected program's intakes
    const program = refSelectedProgram.current
    if (!program) {
      // clear intake options
      setIntakeOptions([IntakeOptionLabel])
      return
    }

    program.intakes.forEach((intake) => {
      if (!tmpIntakeOptions.find((i) => i.name === intake.name)) {
        tmpIntakeOptions.push({
          id: intake.id,
          name: intake.name,
          value: intake.id,
          createdAt: intake.createdAt,
        })
      }
    })

    // sort intake options by date first, otherwise by alphabet ascending
    const sortedTmpIntakeOptions =
      sortIntakeOptionsByCreatedAt(tmpIntakeOptions)

    // console.log('sortedTmpIntakeOptions', sortedTmpIntakeOptions)
    setIntakeOptions([IntakeOptionLabel, ...sortedTmpIntakeOptions])
  }

  const updateCourses = () => {
    setCourses(
      refSelectedIntake.current ? refSelectedIntake.current.courses : [],
    )
    setSelectedCourse(null)
  }

  const updateModules = () => {
    // console.log('updateModules', refSelectedCourse.current?.modules)
    setModules(
      refSelectedCourse.current ? refSelectedCourse.current.modules : [],
    )
    setSelectedModules([])
  }

  const onSelectClient = async (item: DropDownItem) => {
    const value = item.value
    if (value === '0') {
      // label
      return
    } else {
      // selected client
      setIsLoading(true)
      const client = await getArchClient(value)
      setIsLoading(false)

      if (client) {
        setSelectedClient(client)
        refSelectedClient.current = client
        // reset others
        setSelectedProgram(null)
        refSelectedProgram.current = null
        setSelectedIntake(null)
        refSelectedIntake.current = null
        setSelectedCourse(null)
        refSelectedCourse.current = null
        setSelectedModules([])
        refSelectedModules.current = []
      }
    }

    updateProgramOptions()
    updateIntakeOptions()
    updateCourses()
    updateModules()
  }

  const onSelectProgram = (item: DropDownItem) => {
    const value = item.value
    if (value === '0') {
      // label
      return
    } else {
      const program = refSelectedClient.current?.programs.find(
        (p) => p.id === value,
      )
      if (program) {
        setSelectedProgram(program)
        refSelectedProgram.current = program
      }
    }

    // reset intake options
    setSelectedIntake(null)
    // reset dropdown
    refDropDownMenuIntakes.current?.setSelectedItemId(IntakeOptionLabel.id)

    updateIntakeOptions()
    updateCourses()
    updateModules()
  }

  const onSelectIntake = (item: DropDownItem) => {
    const value = item.value
    if (value === '0') {
      // label
      return
    } else {
      const intake = refSelectedClient.current?.programs
        .map((p) => p.intakes)
        .flat()
        .find((i) => i.id === value)
      if (intake) {
        setSelectedIntake(intake)
        refSelectedIntake.current = intake
      }
    }

    updateCourses()
    updateModules()
  }

  const onSelectCourse = (courseId: string) => {
    const course = courses.find((c) => c.id === courseId)
    setSelectedCourse(course || null)
    refSelectedCourse.current = course || null
    // reset selected modules
    setSelectedModules([])
    refSelectedModules.current = []
    updateModules()
    // reset checkedIds
    setCheckedIds([])
  }

  const onSelectMultipleModules = (e: any) => {
    const checked = e.target.checked
    const moduleId = e.target.value
    const module = modules.find((m) => m.id === moduleId)
    const newSelectedModules = []
    if (module) {
      if (checked) {
        // add to selected modules
        newSelectedModules.push(...selectedModules, { ...module })
        setSelectedModules(newSelectedModules)
      } else {
        // remove from selected modules
        newSelectedModules.push(
          ...selectedModules.filter((m) => m.id !== moduleId),
        )
        setSelectedModules(newSelectedModules)
      }
    }

    // console.log('selectedModules', newSelectedModules)
    // get topic ids from topics in selected modules for generating questions
    const topicIds = newSelectedModules
      .map((m) => m.topics)
      .flat()
      .filter((t) => t.id)
      .map((t) => t.id)
    setCheckedIds(topicIds)
  }

  const onSelectSingleModule = (e: any) => {
    const moduleId = e.target.value
    const module = modules.find((m) => m.id === moduleId)
    setSelectedModules(module ? [module] : [])
    setTopics(module ? module.topics : [])
  }

  const renderRow = (topic: ArchTopic, index: number) => {
    return (
      <tr key={index}>
        <td className="mentem-table-cell-2 mentem-title-column pl-1.5">
          <div className="flex flex-row items-center justify-start">
            <input
              type="radio"
              name="topicId"
              className="self-center"
              value={topic.id || ''}
              onChange={(e) => setSelectedTopicId(e.target.value)}
              data-testid={`radio-topic-${index}`}
            />
            {topic.link ? (
              <a
                className="mentem-link pl-2 underline"
                href={topic.link}
                target="_blank"
                rel="noreferrer"
              >
                {topic.name || '(No title)'}
              </a>
            ) : (
              <span className="pl-2">{topic.name || '(No title)'}</span>
            )}
          </div>
        </td>
        <td className="mentem-table-cell-1 text-center">
          <div>{topic.document?.publishedUserName || ''}</div>
        </td>
        <td className="mentem-table-cell-2 text-center">
          <div>
            <div className="flex items-center justify-center">
              {topic.document?.updatedAt &&
                getDateText(topic.document.updatedAt)}
            </div>
            <div className="flex items-center justify-center">
              {topic.document?.updatedAt &&
                getTimeText(topic.document.updatedAt)}
            </div>
          </div>
        </td>
      </tr>
    )
  }

  const fetchClients = useCallback(
    async (silent: boolean = false) => {
      if (!silent) {
        setIsFetchingClients(true)
      }
      try {
        setIsLoading(true)
        setIsFetched(false)
        const clients = await getArchClients()
        // setClients(clients)

        // Set client options
        const allOptions = clients.map((client) => ({
          id: client.id,
          name: client.name,
          value: client.id,
          default: false,
        }))

        // sort by alphabet
        allOptions.sort((a, b) => {
          if (a.name < b.name) {
            return -1
          }
          if (a.name > b.name) {
            return 1
          }
          return 0
        })

        setClientOptions([ClientOptionLabel, ...allOptions])

        updateProgramOptions()
        updateIntakeOptions()
        updateCourses()
        updateModules()
      } catch (error) {
        ToastUtil.error('Failed to fetch clients')
      } finally {
        setIsFetchingClients(false)
        setIsFetched(true)
        setIsLoading(false)
      }
    },
    [getArchClients],
  )

  const onChangeTopic = async () => {
    if (!quizQuestion || !quizId) {
      ToastUtil.warning('No question to change topic')
      return
    }
    if (!selectedTopicId) {
      ToastUtil.warning('Please select a topic')
      return
    }
    // console.log('onChangeTopic', quizQuestion)
    try {
      setIsSaving(true)
      const result = await updateQuizQuestion(quizId, quizQuestion.id, {
        archTopicId: selectedTopicId,
      })
      ToastUtil.success('Topic updated')

      // emit event to update the topic in the question view
      Emitter.emit(Events.UpdatedQuizQuestionTopic, {
        questionId: quizQuestion.id,
        question: result,
      })

      onSaved && onSaved()
    } catch (error) {
      console.error('Failed to change topic', error)
      ToastUtil.error('Failed to change topic')
    } finally {
      setIsSaving(false)
    }
  }

  const fetchCourse = useCallback(async () => {
    try {
      if (!courseId) {
        return
      }
      setIsLoading(true)
      const course = await getArchCourse(courseId)
      if (course) {
        setModules(course.modules)
        setSelectedCourse(course)
      }
    } catch (error) {
      console.error('Failed to fetch course', error)
    } finally {
      // disable loading
      setIsFetchingClients(false)
      setIsFetched(true)
      setIsLoading(false)
    }
  }, [getArchCourse, courseId])

  useEffect(() => {
    if (courseId) {
      // for select course
      fetchCourse()
    } else {
      // for new quiz
      fetchClients()
    }
  }, [fetchCourse, fetchClients, courseId])

  return (
    <div
      className="absolute z-[999] flex h-full w-full flex-col items-center justify-center bg-[#D5D5DBAA]"
      data-testid={dataTestId}
    >
      <div
        className="flex max-h-[90%] min-h-[50%] w-3/4 flex-col gap-[12px] bg-white p-[24px] pb-[24px] shadow-xl"
        data-testid="widget-question-generator-content"
      >
        <img
          src={closeSvg}
          className="cursor-pointer self-end"
          alt="close"
          onClick={onClose}
          data-testid="btn-close-widget"
        />
        {isGenerating && (
          <LargeSpinner
            text="Generating questions. This may take a couple of minutes, please be patient."
            data-testid="spinner-generating-questions"
          />
        )}
        {!isGenerating && isFetched && (
          <>
            <div
              className={`flex w-full flex-col items-start justify-start gap-4 px-[12px] ${
                multiple ? 'max-h-[90%]' : 'max-h-[50%]'
              }`}
            >
              {!isFetchingClients && (
                <>
                  {!courseId && (
                    <div
                      className="flex w-full flex-row items-center gap-[12px]"
                      data-testid="architecture-selectors"
                    >
                      <div className="w-[20%]">
                        <DropDownMenu
                          items={clientOptions}
                          onSelected={onSelectClient}
                          ref={refDropDownMenuClients}
                          data-testid="dropdown-client"
                        />
                      </div>
                      <div className="w-[60%]">
                        <DropDownMenu
                          items={programOptions}
                          onSelected={onSelectProgram}
                          ref={refDropDownMenuPrograms}
                          data-testid="dropdown-program"
                        />
                      </div>
                      <div className="w-[20%]">
                        <DropDownMenu
                          items={intakeOptions}
                          onSelected={onSelectIntake}
                          ref={refDropDownMenuIntakes}
                          data-testid="dropdown-intake"
                        />
                      </div>
                    </div>
                  )}
                  <div className="flex max-h-[100%] w-full flex-col gap-[12px] overflow-auto">
                    {selectedClient && selectedProgram && selectedIntake && (
                      <div
                        className="flex w-full flex-wrap gap-[12px]"
                        data-testid="course-list"
                      >
                        {courses?.map((course, index) => (
                          <div
                            key={index}
                            className="flex cursor-pointer items-center justify-start gap-[6px]"
                          >
                            <input
                              id={`course-${course.id}`}
                              type="radio"
                              name="courseId"
                              className="radio-primary"
                              value={course.id}
                              checked={selectedCourse?.id === course.id}
                              onChange={(e) => onSelectCourse(e.target.value)}
                              data-testid={`radio-course-${index}`}
                            />
                            <div
                              className="flex flex-row gap-[4px] font-roboto text-sm font-semibold normal-case"
                              onClick={() => {
                                // simulate to select radio button by getting the id
                                // console.log('course.id', course.id)
                                const id = `course-${course.id}`
                                const radio = document.getElementById(id)
                                if (radio) {
                                  radio.click()
                                }
                              }}
                            >
                              <span className="text-purple">
                                Course {(index + 1).toString().padStart(2, '0')}
                                :
                              </span>
                              <span>{course.name}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedCourse && modules && modules.length > 0 && (
                      <div
                        className="flex w-full flex-wrap gap-[12px]"
                        data-testid="module-list"
                      >
                        {modules.map((module, index) => (
                          <div
                            key={index}
                            className="flex cursor-pointer items-center justify-start gap-[6px]"
                          >
                            {multiple ? (
                              <input
                                id={`module-${module.id}`}
                                type="checkbox"
                                value={module.id}
                                checked={selectedModules.some(
                                  (m) => m.id === module.id,
                                )}
                                onChange={(e) => {
                                  onSelectMultipleModules(e)
                                }}
                                data-testid={`checkbox-module-${index}`}
                              />
                            ) : (
                              <input
                                id={`module-${module.id}`}
                                type="radio"
                                name="moduleId"
                                className="radio-small"
                                value={module.id}
                                checked={selectedModules.some(
                                  (m) => m.id === module.id,
                                )}
                                onChange={(e) => {
                                  onSelectSingleModule(e)
                                }}
                                data-testid={`radio-module-${index}`}
                              />
                            )}
                            <span
                              className="font-roboto text-sm font-semibold normal-case"
                              onClick={() => {
                                // simulate to select input by getting the id
                                // console.log('module.id', module.id)
                                const id = `module-${module.id}`
                                const input = document.getElementById(
                                  id,
                                ) as HTMLInputElement
                                if (input) {
                                  input.click()
                                }
                              }}
                            >
                              Module {(index + 1).toString().padStart(2, '0')}:{' '}
                              {module.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {isFetched &&
                selectedIntake &&
                courses &&
                courses.length === 0 && (
                  <p className="self-center" data-testid="msg-no-courses">
                    No courses available
                  </p>
                )}

              {isFetched &&
                selectedIntake &&
                selectedCourse &&
                modules &&
                modules.length === 0 && (
                  <p className="self-center" data-testid="msg-no-modules">
                    No modules available
                  </p>
                )}
            </div>

            {isFetched &&
              !multiple &&
              selectedModules &&
              selectedModules.length > 0 && (
                <div
                  className="my-[6px] max-h-[50%] min-h-[30%] grow overflow-auto"
                  data-testid="topics-table-container"
                >
                  <table className="mentem-table" data-testid="table-topics">
                    <thead>
                      <tr>
                        <th
                          className={`${StyleUtil.stickyTableHeader1} h-[55px] w-[50%] min-w-[200px]`}
                        >
                          <div className="flex flex-row items-center px-2">
                            <span className="grow">Existing topics</span>
                            <img
                              className="cursor-pointer"
                              src={sortSvg}
                              alt="sort"
                              onClick={() => onClickSort(SortBy.Topic)}
                              data-tooltip-id="tooltip-topic"
                            />
                          </div>
                        </th>
                        <th
                          className={`${StyleUtil.stickyTableHeader2} w-[25%] min-w-[150px]`}
                        >
                          <div className="flex flex-row items-center px-2">
                            <span className="grow">Created by</span>
                            <img
                              className="cursor-pointer"
                              src={sortSvg}
                              alt="sort"
                              onClick={() => onClickSort(SortBy.PublishedBy)}
                              data-tooltip-id="tooltip-published-by"
                            />
                          </div>
                        </th>
                        <th
                          className={`${StyleUtil.stickyTableHeader1} w-[25%] min-w-[160px]`}
                        >
                          <div className="flex flex-row items-center px-2">
                            <span className="grow">Last updated</span>
                            <img
                              className="cursor-pointer pl-2 pr-2"
                              src={sort2Svg}
                              alt="sort"
                              onClick={() => onClickSort(SortBy.DateTime)}
                              data-tooltip-id="tooltip-date-time"
                            />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {topics.map((topic, index: number) => {
                        return renderRow(topic, index)
                      })}
                    </tbody>
                  </table>
                  <Tooltip
                    id="tooltip-topic"
                    className="mentem-tooltip"
                    style={getTooptipStyle(SortBy.Topic)}
                    place="top"
                    noArrow={true}
                  >
                    {getSortingText(SortBy.Topic)}
                  </Tooltip>
                  <Tooltip
                    id="tooltip-published-by"
                    className="mentem-tooltip"
                    style={getTooptipStyle(SortBy.PublishedBy)}
                    place="top"
                    noArrow={true}
                  >
                    {getSortingText(SortBy.PublishedBy)}
                  </Tooltip>
                  <Tooltip
                    id="tooltip-date-time"
                    className="mentem-tooltip"
                    style={getTooptipStyle(SortBy.DateTime)}
                    place="top"
                    noArrow={true}
                  >
                    {getSortingText(SortBy.DateTime)}
                  </Tooltip>
                </div>
              )}

            <div className="flex w-full flex-row items-center justify-center gap-4">
              {multiple && (
                <>
                  <p
                    className="mentem-widget-title"
                    data-testid="label-number-of-questions"
                  >
                    Number of questions to generate
                  </p>
                  <DropDownMenu
                    items={[
                      // start from 10 to 30 numbers interval 5
                      ...Array.from({ length: 5 }, (_, i) => i).map((i) => {
                        const value = i * 5 + 10
                        return {
                          id: value.toString(),
                          name: value.toString(),
                          value: value,
                          default: value === 30,
                        }
                      }),
                    ]}
                    onSelected={onSelectNumberOfQuestions}
                    data-testid="dropdown-number-of-questions"
                  />
                </>
              )}
              <div className="grow" />
              {isChangingTopic ? (
                <button
                  type="button"
                  className={StyleUtil.buttonPrimaryUnsw}
                  onClick={onChangeTopic}
                  disabled={isSaving}
                  data-testid="btn-save-topic"
                >
                  {isSaving ? <ButtonSpinner /> : 'Save'}
                </button>
              ) : (
                <button
                  type="button"
                  className={StyleUtil.buttonPrimaryUnsw}
                  onClick={onClickGenerate}
                  disabled={isGenerating || !canGenerate()}
                  data-testid={
                    multiple ? 'btn-auto-generate' : 'btn-generate-question'
                  }
                >
                  {isGenerating ? (
                    <ButtonSpinner />
                  ) : multiple ? (
                    'Auto-generate'
                  ) : (
                    'Generate question'
                  )}
                </button>
              )}
            </div>
          </>
        )}
        {isLoading && <Spinner data-testid="spinner-loading" />}
      </div>
    </div>
  )
}
